<template>
  <div class="form-group">
    <label v-if="label" for="inputId" :aria-label="label" class="form-label">
      {{ label }}
      <span v-if="optionalLabel" class="optional-label">{{
        optionalLabel
      }}</span>
      <span v-if="required" class="required"></span>
    </label>
    <div
      :class="[
        `form-input`,
        showVanityStyle && `form-input--vanity`,
        error && `form-input--error`,
        icon && iconPosition === 'left' && `form-input--icon--left`,
        icon && iconPosition === 'right' && `form-input--icon--right`,
        supportingText &&
          supportingTextPosition === 'prefix' &&
          `form-input--icon--left`,
        supportingText &&
          supportingTextPosition === 'suffix' &&
          `form-input--icon--right`,
        showClearButton && `form-input--clear-button`,
      ]"
    >
      <icon v-if="icon" class-name="form-input__icon" :name="icon" />
      <span v-if="supportingText" class="form-input__icon">{{
        supportingText
      }}</span>
      <input
        :id="id"
        :ref="id"
        :type="type"
        autocorrect="off"
        autocomplete="off"
        autocapitalize="none"
        spellcheck="false"
        :placeholder="placeholder"
        :class="[`form-input__field`, hover && `hover`, focus && `focus`]"
        :disabled="disabled"
        :value="value"
        @keydown="handleValidation($event)"
        @input="$emit('onChange', $event)"
      />
      <a
        v-if="showClearButton"
        class="form-input__clear"
        aria-hidden="true"
        @click="$emit('onClear')"
      >
        <icon class-name="form-input__clear__icon" name="times-circle" />
      </a>
    </div>
    <p
      v-if="errorMessage"
      class="form-label form-label--error form-label--error--active"
    >
      {{ errorMessage }}
    </p>
    <div
      v-if="helpMessage"
      :class="[`form-help`, helpMessagePosition === `right` && `text-right`]"
    >
      {{ helpMessage }}
    </div>
  </div>
</template>

<script>
// Components
import Icon from "../Icon/Icon.vue";

export default {
  name: "TextInput",
  components: {
    Icon,
  },
  props: {
    /**
     * @description
     * Provides Label for Text Input
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Placeholder for Text Input
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Icon Name for Text Input
     */
    icon: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Supporting Text for Text Input
     * Use Valid Small String that can be placed on the left side of the placeholder
     * For example:  $
     */
    supportingText: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Optional Label for Text Input
     */
    optionalLabel: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Error Message for Text Input
     */
    errorMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Help Message for Text Input
     */
    helpMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Checks the Text Input help message position
     * Maersk Design System supports left and right
     */
    helpMessagePosition: {
      type: String,
      validator: (val) => ["left", "right"].includes(val),
      default: "left",
    },
    /**
     * @description
     * Checks the Text Input icon position
     * Maersk Design System supports left and right
     */
    iconPosition: {
      type: String,
      validator: (val) => ["left", "right"].includes(val),
      default: "left",
    },
    /**
     * @description
     * Checks the supporting Text Position
     * Maersk Design System supports left and right
     */
    supportingTextPosition: {
      type: String,
      validator: (val) => ["prefix", "suffix"].includes(val),
      default: "prefix",
    },
    /**
     * @description
     * Emits the value change on input, Parent Component needs to sync values emitted
     */
    value: {
      type: String,
      required: true,
    },
    /**
     * @description
     * Checks if the Text Input should be of Vanity Style
     */
    showVanityStyle: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Text Input should be Disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Text Input should be Hoverable
     */
    hover: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Text Input should be Focusable
     */
    focus: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Text Input should display Error Outline
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Text Input should display Clear Button
     */
    showClearButton: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Passes custom validation that can be updated based on the requirement
     * Pass the name of the validation as same as from validation object";
     */
    validation: {
      type: String,
      default: "",
    },
     /**
     * @description
     * used for custom validation based on validaton name
     */
    validationObject: {
      type: Object,
      default: () => {},
    },
    id: {type: String, required: true},
    required: { type: Boolean, default: false },
    type: {type: String, default: "text" }
  },
  mounted() {
    if(this.focus) {
      this.$refs[`${this.id}`].focus();
    }
  },
   methods: {
    handleValidation(event) {
      if (this.validation.length) {
        this.validationObject[`${this.validation}`](event);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form-group {
  .required:after {
    content: " *";
    color: var(--Danger);
  }
}
</style>